/**
 * The client half of the shortage identity contract.
 *
 * Its server half is backend/utils/shortageIdentity.js — read that file's
 * docblock first; this one only explains the client's obligation under it.
 *
 * THE CONTRACT
 * ------------
 * Every shortage row the server publishes carries `match_keys`: the exact
 * tokens it will accept back as "the user confirmed negative stock for this".
 * Confirming a shortage means echoing THOSE tokens. Not the label they were
 * rendered from, not a key rebuilt out of the label.
 *
 * THE BUG THIS EXISTS TO PREVENT
 * ------------------------------
 * "Confirm & Create Negative Stock" rebuilt its own key instead —
 * `<item scope>|<ingredient label>` — and sent that. For a /wastage/batch
 * submission the item scope was always the string `this item`: the batch
 * pre-flight sums every item's requirement before anything is written, so the
 * 400 it returns belongs to the submission rather than to one row and carries
 * no item index for the client to name. The server scoped the same resolution
 * as `id:<menu_item_id>|…`, the two never met, and so:
 *
 *   confirm -> resubmit -> the identical shortage -> the identical modal
 *
 * forever. No wastage was recorded and no negative stock was ever created,
 * because the submission never got past validation to the point where
 * deductLines() opens a NEGATIVE_ADJUSTMENT batch.
 *
 * `match_keys` are unscoped, which is also the right semantics for this modal:
 * it shows ONE merged list for the whole submission, so the user is confirming
 * an ingredient for the submission, not for a single dish — exactly how the
 * server's batch pre-flight already treats a resolution.
 */

/**
 * The tokens to send back for a shortage row the user confirmed.
 *
 * @param {{name?: string, match_keys?: string[]}} ingredient - a row as published
 *   in the 400's `details`
 * @returns {string[]} tokens for `resolvedIngredients`; empty when the row
 *   carries no usable identity, which the caller must treat as "cannot confirm"
 *   rather than submitting and being re-blocked.
 */
export const shortageMatchTokens = (ingredient) => {
  const published = Array.isArray(ingredient?.match_keys) ? ingredient.match_keys : [];
  const tokens = published
    .map((token) => String(token || '').trim().toLowerCase())
    // A '|' would make the server read the token as an item-scoped key, which
    // could never match. No published key contains one; guard anyway.
    .filter((token) => token && !token.includes('|'));
  if (tokens.length > 0) return tokens;
  // A server that predates match_keys publishes only the label. It is then the
  // one identity available, and the server's legacy path accepts it.
  const label = String(ingredient?.name || '').trim().toLowerCase();
  return label && !label.includes('|') ? [label] : [];
};

/**
 * The item a tick belongs to: the shortage modal shows one item at a time, and
 * selections made against a previous one must not be read as this one's.
 *
 * Its own function because the confirm handler needs the scope WITHOUT an
 * ingredient, to filter the selection map. Computing it separately there is how
 * a scope of `name:undefined` came to be compared against ticks stored under
 * `name:Unknown Item` — every selection filtered out, and the button insisting
 * nothing was selected when three rows were.
 */
export const buildShortageItemScope = ({ itemName, menuItemId } = {}) =>
  (menuItemId !== null && menuItemId !== undefined && `${menuItemId}` !== '')
    ? `id:${menuItemId}`
    : `name:${itemName || 'Unknown Item'}`;

/**
 * The key a shortage row is ticked under in the modal.
 *
 * Purely client-side — it scopes a checkbox to the item the modal is showing —
 * and deliberately NOT what is sent to the server. Shared by the modal and the
 * confirm handler so a tick and its ingredient cannot be looked up under
 * different keys.
 */
export const buildShortageSelectionKey = (ingredient, fallback = {}) => {
  const scope = buildShortageItemScope({
    itemName: ingredient?.itemName || fallback.itemName,
    menuItemId: ingredient?.menuItemId ?? fallback.menuItemId,
  });
  return `${scope}|${ingredient?.name || 'Unknown Ingredient'}`;
};
